import { useState, useEffect } from "react";
import { deleteAttraction, fetchAttractions as fetchAttractionsApi, getAttraction, type AttractionListItem } from "@/lib/attractionsApi";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Eye, Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AttractionForm from "./AttractionForm";
import AttractionViewModal from "./AttractionViewModal";
import TicketViewModal from "./TicketViewModal";

type Attraction = AttractionListItem;

type SortField = "title" | "attraction_code" | null;
type SortOrder = "asc" | "desc";

const AttractionsList = () => {
  const { userRole } = useAuth();
  const [attractions, setAttractions] = useState<Attraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState<Attraction | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAttractionId, setSelectedAttractionId] = useState<number | null>(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicketAttractionId, setSelectedTicketAttractionId] = useState<number | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { toast } = useToast();
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchAttractions = async () => {
    try {
      setLoading(true);
      const payload = await fetchAttractionsApi({
        page: currentPage,
        size: pageSize,
        search: debouncedSearchTerm || undefined,
        sortField,
        sortOrder,
      });

      setAttractions(payload.items || []);
      setTotalCount(payload.total || 0);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttractions();
  }, [debouncedSearchTerm, currentPage, pageSize, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleView = (attractionId: number) => {
    setSelectedAttractionId(attractionId);
    setViewModalOpen(true);
  };

  const handleEdit = async (attraction: Attraction, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const attractionData = await getAttraction(attraction.id);
      setSelectedAttraction(attractionData);
      setIsFormOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteAttraction(deleteId);
      toast({ title: "Success", description: "Attraction deleted successfully" });
      fetchAttractions();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleViewTicket = (ticketId: number, attractionId: number) => {
    setSelectedTicketId(ticketId);
    setSelectedTicketAttractionId(attractionId);
    setTicketModalOpen(true);
  };

  const canEdit = Boolean(userRole);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Attractions</h2>
        {canEdit && (
          <Button onClick={() => { setSelectedAttraction(null); setIsFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Attraction
          </Button>
        )}
      </div>

      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search by code, title, or supplier..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-10"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("attraction_code")}
                  className="h-8 px-2"
                >
                  Code
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "attraction_code" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("title")}
                  className="h-8 px-2"
                >
                  Title
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "title" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Tickets</TableHead>
              {canEdit && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="text-center text-muted-foreground h-24">
                  Loading attractions...
                </TableCell>
              </TableRow>
            ) : attractions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="text-center text-muted-foreground h-24">
                  No attractions found
                </TableCell>
              </TableRow>
            ) : (
              attractions.map((attraction) => (
                <TableRow 
                  key={attraction.id}
                  onClick={() => handleView(attraction.id)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{attraction.attraction_code}</TableCell>
                  <TableCell>{attraction.title}</TableCell>
                  <TableCell>{attraction.supplier_name || "-"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {attraction.tickets?.map((ticket) => (
                        <Button
                          key={ticket.id}
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTicket(ticket.id, attraction.id);
                          }}
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          {ticket.title}
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleEdit(attraction, e)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(attraction.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm">
            Page {currentPage} of {totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isFormOpen && (
        <AttractionForm
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsFormOpen(false);
              setSelectedAttraction(null);
            }
          }}
          initialData={selectedAttraction}
          onSuccess={() => {
            setIsFormOpen(false);
            setSelectedAttraction(null);
            fetchAttractions();
          }}
        />
      )}

      {viewModalOpen && selectedAttractionId && (
        <AttractionViewModal
          attractionId={selectedAttractionId}
          open={viewModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setViewModalOpen(false);
              setSelectedAttractionId(null);
            }
          }}
        />
      )}

      {ticketModalOpen && selectedTicketId && selectedTicketAttractionId && (
        <TicketViewModal
          ticketId={selectedTicketId}
          attractionId={selectedTicketAttractionId}
          open={ticketModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setTicketModalOpen(false);
              setSelectedTicketId(null);
              setSelectedTicketAttractionId(null);
            }
          }}
        />
      )}

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the attraction and all its tickets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AttractionsList;
